import "../public/static/app.css";
import type { NextPage } from "next";
import dynamic from "next/dynamic";
import Head from "next/head";
import "raf/polyfill";
import { type ReactElement, type ReactNode, useCallback } from "react";
import type { SolitoAppProps } from "solito";
import "@tamagui/core/reset.css";
// Must stay directly below the reset import — it overrides the reset's
// min-height:100vh (mis-anchored on iOS) with 100dvh at equal specificity.
import "../public/static/reset-overrides.css";
import "@tamagui/font-inter/css/400.css";
import "@tamagui/font-inter/css/700.css";
import { StytchProvider } from "@stytch/react";
import { createStytchHeadlessClient } from "@stytch/react/headless";
import { type ColorScheme, NextThemeProvider, useRootTheme } from "@tamagui/next-theme";
import { DownloadAppSheet } from "@medbillai/app/components/DownloadAppSheet";
import { ShadowLoginBanner } from "@medbillai/app/components/ShadowLoginBanner";
import { LogoutReason } from "@medbillai/app/lib/analytics";
import { AuthProvider } from "@medbillai/app/lib/auth";
import { EmailLinkAnalyticsTracker } from "@medbillai/app/lib/email-tracking/emailLinkAnalyticsTracker";
import { FlagProvider } from "@medbillai/app/lib/flag";
import { ScreenLockProvider } from "@medbillai/app/lib/screen-lock";
import { useLogout } from "@medbillai/app/lib/useLogout";
import { Provider } from "@medbillai/app/provider";
import "../public/static/fonts.css";
import KHTekaLight from "../public/static/fonts/kh-teka/KHTeka-Light.woff2";
import KHTekaMedium from "../public/static/fonts/kh-teka/KHTeka-Medium.woff2";
import KHTekaRegular from "../public/static/fonts/kh-teka/KHTeka-Regular.woff2";
import "../sentry.config";
import type { AuthenticateMethod } from "@medbillai/generated-graphql-types";
import { createAuthRedirectUri } from "@medbillai/utils/urls";
import { ErrorBoundary } from "../utils/ErrorBoundary";
import { useAnalyticsService } from "../utils/UseAnalyticsService";
import { useSingularService } from "../utils/UseSingularService";
import { useObservability } from "../utils/useObservability";
if (process.env.NODE_ENV === "production") {
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    require("../public/static/tamagui.css");
  } catch {}
}
const noOverlayWorkaroundScript = `
  window.addEventListener('error', event => {
    event.stopImmediatePropagation()
  })

  window.addEventListener('unhandledrejection', event => {
    event.stopImmediatePropagation()
  })
`;
const stytch = createStytchHeadlessClient(process.env.NEXT_PUBLIC_STYTCH_PUBLIC_KEY ?? "");

// Opt-in per developer (not NODE_ENV) so the feedback toolbar doesn't show for the
// whole team locally. Enable with NEXT_PUBLIC_AGENTATION=true in .env.development.local.
// The non-production floor guarantees it can never surface in a prod build even if the
// flag leaks into a deployed env, and keeps the package tree-shaken out of prod bundles.
const Agentation = process.env.NODE_ENV !== "production" && process.env.NEXT_PUBLIC_AGENTATION === "true" ? dynamic(() => import("agentation").then(m => m.Agentation), {
  ssr: false
}) : null;

// Ref: https://nextjs.org/docs/pages/building-your-application/routing/pages-and-layouts
export type NextPageWithLayout<P = object, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode;
};
function MyApp({
  Component,
  pageProps
}: SolitoAppProps) {
  useObservability();
  useAnalyticsService();
  useSingularService();
  const getLayout = Component.getLayout || (page => page);
  const [_theme, setTheme] = useRootTheme({
    fallback: "light"
  });
  const logout = useLogout();
  const onAuthError = useCallback(() => {
    // TODO: Pass redirect path?
    logout({
      reason: LogoutReason.AUTH_ERROR
    });
    return Promise.resolve();
  }, [logout]);

  // Since window is globally available and immutable in this context,
  // you won't run into stale closure issues by not including it in the dependency array
  const authRedirectUri = useCallback((method: AuthenticateMethod) => {
    if (typeof window === "undefined") {
      console.log("No window object");
      return ""; // fallback for SSR
    }
    const currentUrl = new URL(typeof window === "undefined" ? "" : window.location.href);
    return createAuthRedirectUri({
      baseUri: currentUrl.origin,
      platform: "web",
      method: method
    });
  }, []);
  return <>
      <Head data-sentry-element="Head" data-sentry-source-file="_app.tsx">
        <title>Granted</title>
        {/**
         * Smart app banner for iOS
         * https://developer.apple.com/documentation/webkit/promoting-apps-with-smart-app-banners
         */}
        <meta name="apple-itunes-app" content="app-id=6473135975" data-sentry-element="meta" data-sentry-source-file="_app.tsx" />
        <meta name="description" content="Granted" data-sentry-element="meta" data-sentry-source-file="_app.tsx" />
        <link rel="icon" href="/favicon.ico" />
        {/*
          Note that only the core typography fonts used on the home screen need
          to be preloaded! Each font added here will delay initial rendering of
          the screen, which prevents a "flash" of an incorrect font, but creates
          initial latency. Additional non-core fonts will be loaded on-demand.
         */}
        <link rel="preload" href={KHTekaLight} as="font" type="font/woff2" crossOrigin="anonymous" />
        <link rel="preload" href={KHTekaRegular} as="font" type="font/woff2" crossOrigin="anonymous" />
        <link rel="preload" href={KHTekaMedium} as="font" type="font/woff2" crossOrigin="anonymous" />
        {process.env.NODE_ENV !== "production" && process.env.SUPPRESS_NEXT_ERROR_OVERLAY === "1" ? <script
      // biome-ignore lint/security/noDangerouslySetInnerHtml: Needed to disable the overlay
      dangerouslySetInnerHTML={{
        __html: noOverlayWorkaroundScript
      }} /> : null}
      </Head>
      <ErrorBoundary name="root" data-sentry-element="ErrorBoundary" data-sentry-source-file="_app.tsx">
        <NextThemeProvider forcedTheme="light" defaultTheme="light" enableSystem={false} onChangeTheme={next => {
        setTheme(next as ColorScheme);
      }} data-sentry-element="NextThemeProvider" data-sentry-source-file="_app.tsx">
          <FlagProvider data-sentry-element="FlagProvider" data-sentry-source-file="_app.tsx">
            <StytchProvider stytch={stytch} assumeHydrated={false} data-sentry-element="StytchProvider" data-sentry-source-file="_app.tsx">
              <Provider apolloPath="/api/graphql" apolloWsPath="/api/subscriptions" onAuthError={onAuthError} enableDevTools={process.env.NODE_ENV !== "production"} data-sentry-element="Provider" data-sentry-source-file="_app.tsx">
                <ScreenLockProvider data-sentry-element="ScreenLockProvider" data-sentry-source-file="_app.tsx">
                  <AuthProvider authRedirectUri={authRedirectUri} data-sentry-element="AuthProvider" data-sentry-source-file="_app.tsx">
                    <EmailLinkAnalyticsTracker data-sentry-element="EmailLinkAnalyticsTracker" data-sentry-source-file="_app.tsx" />
                    <ShadowLoginBanner data-sentry-element="ShadowLoginBanner" data-sentry-source-file="_app.tsx" />
                    <DownloadAppSheet data-sentry-element="DownloadAppSheet" data-sentry-source-file="_app.tsx" />
                    {Agentation ? <Agentation endpoint="http://localhost:4747" /> : null}
                    {getLayout(<Component {...pageProps} />)}
                  </AuthProvider>
                </ScreenLockProvider>
              </Provider>
            </StytchProvider>
          </FlagProvider>
        </NextThemeProvider>
      </ErrorBoundary>
    </>;
}
export default MyApp;